import type { WizardStep } from '@/components/wizards';
import { CredentialType } from '@/sections/asset/components/tabs/credentials/credentialForms';
import { WIZARD_STEPS as RECIPE_STEPS } from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';
import { SeedTypeOption } from '@/types';

import type { SeedWizardFormData } from './AddSeedByTypeModal.types';
import {
  parseSeedCount,
  parseSeedLines,
  resolveAppCredential,
} from './steps/credentialAssign.util';
import { collectFieldErrors } from './steps/credentialStep.util';

/**
 * Step ID constants for switch statements.
 * Using constants prevents typo bugs and enables IDE autocomplete.
 */
export const STEP_IDS = {
  INTRO: 'intro',
  SELECT_TYPE: 'select-type',
  ADD_SEEDS: 'add-seeds',
  CREDENTIAL: 'credential',
  // Multi-app credential assignment — shown only on the seedCount > 1 web-seed
  // `configure-multi` path. Replaces the single-seed CREDENTIAL_APPROACH +
  // CREDENTIAL_FORM steps (both gated off when seedCount > 1).
  CREDENTIAL_ASSIGN: 'credential-assign',
  CREDENTIAL_APPROACH: 'credential-approach',
  CREDENTIAL_FORM: 'credential-form',
  // Recipe sub-steps — RecipeWizard's 5 steps lifted into THIS wizard so the
  // RECIPE path is a natural progression of the parent (one footer, one
  // stepper). Prefixed `recipe-*` to avoid id collision with RecipeWizard's own
  // STEP_IDS (overview/inputs/...) and with the parent's existing ids.
  RECIPE_OVERVIEW: 'recipe-overview',
  RECIPE_INPUTS: 'recipe-inputs',
  RECIPE_REQUEST: 'recipe-request',
  RECIPE_SESSION: 'recipe-session',
  RECIPE_REVIEW: 'recipe-review',
} as const;

/** True for web-app / web-service seeds (the only types with a credential step). */
export const isWebSeed = (data: SeedWizardFormData): boolean =>
  data.seedType === SeedTypeOption.WEB_APPLICATION ||
  data.seedType === SeedTypeOption.WEB_SERVICE;

/**
 * True only when the user is on the RECIPE credential path: a web seed, in
 * configure mode, with RECIPE selected. Gates the 5 recipe sub-steps.
 */
const isConfiguringRecipe = (data: SeedWizardFormData): boolean =>
  isWebSeed(data) &&
  data.credential.mode === 'configure' &&
  data.credential.data.selectedType === CredentialType.RECIPE;

/**
 * Adapts a RecipeWizard step validator (operates on RecipeWizardData) to the
 * parent wizard's SeedWizardFormData by reading credential.data.recipeDraft.
 * Returns true in skip mode (no recipe draft to validate). recipeStepIndex maps
 * to RECIPE_STEPS positions: 0=Overview, 1=Inputs, 2=Request, 3=Session, 4=Review.
 */
const recipeValidate =
  (recipeStepIndex: number) =>
  (data: SeedWizardFormData): boolean => {
    if (data.credential.mode !== 'configure') return true;
    return (
      RECIPE_STEPS[recipeStepIndex].validate?.(
        data.credential.data.recipeDraft
      ) ?? true
    );
  };

/**
 * Gates the credential FORM and Login-Inputs steps on the shared FE validators
 * (P1-4) via the same {@link collectFieldErrors} the step renders inline: a step
 * with field errors (CR/LF in a header value, a non-http(s) login URL, …) cannot
 * advance or submit. Returns true in skip mode (no credential data to validate),
 * keeping credentials optional — only an *invalid* configured credential blocks.
 */
const credentialFieldsValid = (data: SeedWizardFormData): boolean => {
  if (data.credential.mode === 'configure') {
    return collectFieldErrors(data.credential.data).length === 0;
  }
  if (data.credential.mode === 'configure-multi') {
    const { shared, perApp, custom } = data.credential;
    const appUrls = parseSeedLines(data.textInput);
    // Valid when every assigned row resolves to a clean config. 'off' rows
    // resolve to null (seed created without a credential — still valid). A
    // 'shared'/'custom' row with no base bundle (e.g. shared === null) cannot
    // resolve → invalid. A resolved bundle with field errors → invalid.
    return appUrls.every(url => {
      const mode = perApp[url] ?? 'off';
      if (mode === 'off') return true;
      const resolved = resolveAppCredential(mode, shared, custom[url], url);
      if (!resolved) return false;
      return collectFieldErrors(resolved).length === 0;
    });
  }
  return true;
};

/**
 * Wizard step configuration for Add Seed modal.
 * Wizard for web apps: intro -> type selection -> data entry -> credential.
 * The intro is a context splash shown first (never blocks forward nav).
 * Non-web-app types skip the credential step via shouldSkip.
 */
export const seedWizardSteps: WizardStep<SeedWizardFormData>[] = [
  {
    id: STEP_IDS.INTRO,
    order: 0,
    title: 'Welcome',
    description: 'Add seeds to your attack surface',
    // Intro is informational — never blocks forward navigation.
    validate: () => true,
  },
  {
    id: STEP_IDS.SELECT_TYPE,
    order: 1,
    title: 'Select Type',
    description: 'Choose the type of seed to add',
    validate: data => data.seedType !== null,
  },
  {
    id: STEP_IDS.ADD_SEEDS,
    order: 2,
    title: 'Add Seeds',
    description: 'Enter seed data or upload files',
    // NOTE: Cannot fully validate here - file state is external.
    // Full validation handled by canSubmit in modal (Hybrid State Pattern).
    validate: () => true,
  },
  {
    id: STEP_IDS.CREDENTIAL,
    order: 3,
    title: 'Authentication',
    description: 'Choose whether to configure authentication',
    shouldSkip: data =>
      data.seedType !== SeedTypeOption.WEB_APPLICATION &&
      data.seedType !== SeedTypeOption.WEB_SERVICE,
    // Credentials are optional — never block Next/Finish.
    validate: () => true,
  },
  {
    id: STEP_IDS.CREDENTIAL_ASSIGN,
    // Fractional order sorts between CREDENTIAL (3) and CREDENTIAL_APPROACH (4)
    // via the existing numeric `.sort((a, b) => a.order - b.order)`.
    order: 3.5,
    title: 'Assign Credentials',
    description: 'Assign authentication to each application',
    // Shown ONLY on the multi-app path: a web seed, in configure-multi mode,
    // with more than one seed in the textarea. Single-seed (or non-web, or
    // skip/configure) flows keep the single-seed CREDENTIAL_APPROACH/FORM steps.
    shouldSkip: data =>
      !(
        isWebSeed(data) &&
        data.credential.mode === 'configure-multi' &&
        parseSeedCount(data.textInput) > 1
      ),
    // Multi-aware gate: an assigned row that can't resolve (shared === null) or
    // resolves to a credential with field errors blocks Add Seeds.
    validate: credentialFieldsValid,
  },
  {
    id: STEP_IDS.CREDENTIAL_APPROACH,
    order: 4,
    title: 'Credentials',
    description: 'Choose an authentication method',
    // Shown only for web seeds AND only when the user chose "Configure" on the
    // preceding step. This screen shows ONLY the approach-selection cards; the
    // chosen approach's form lives on the following CREDENTIAL_FORM step so each
    // wizard step stays small (bite-size).
    shouldSkip: data =>
      (data.seedType !== SeedTypeOption.WEB_APPLICATION &&
        data.seedType !== SeedTypeOption.WEB_SERVICE) ||
      data.credential.mode !== 'configure' ||
      parseSeedCount(data.textInput) > 1,
    validate: () => true,
  },
  {
    id: STEP_IDS.CREDENTIAL_FORM,
    order: 5,
    title: 'Configure',
    description: 'Provide credentials for authenticated scanning',
    // Same gating as CREDENTIAL_APPROACH (web seed + configure mode), PLUS skip
    // for RECIPE — the recipe path renders the 5 recipe sub-steps below instead
    // of this single-screen form. COMPLEX_LOGIN now renders its full
    // multi-identity manager on THIS single screen (Phase 4 dissolved the old
    // two-step basics→login-inputs flow). For every non-RECIPE configure type
    // this stays the last visible step, so position-derived isLastStep gives it
    // "Add Seeds". (No isFinal: it's decorative and would mislead now that
    // RECIPE_REVIEW is the terminal step on the recipe path.)
    shouldSkip: data =>
      !isWebSeed(data) ||
      data.credential.mode !== 'configure' ||
      parseSeedCount(data.textInput) > 1 ||
      data.credential.data.selectedType === CredentialType.RECIPE,
    // Gate on the authored FE validators (P1-4): a configured credential with
    // field errors (bad header value) or an invalid/incomplete COMPLEX_LOGIN
    // identity set can't advance/submit.
    validate: credentialFieldsValid,
  },
  // Recipe sub-steps (orders 7–11) — shown only on the RECIPE path. Each reuses
  // the corresponding RecipeWizard validator via recipeValidate(index).
  // RECIPE_REVIEW is the terminal step when recipe is active → position-derived
  // isLastStep gives it the "Add Seeds" submit button.
  {
    id: STEP_IDS.RECIPE_OVERVIEW,
    order: 7,
    title: 'Overview',
    shouldSkip: data => !isConfiguringRecipe(data),
    validate: recipeValidate(0),
  },
  {
    id: STEP_IDS.RECIPE_INPUTS,
    order: 8,
    title: 'Login Credentials',
    shouldSkip: data => !isConfiguringRecipe(data),
    validate: recipeValidate(1),
  },
  {
    id: STEP_IDS.RECIPE_REQUEST,
    order: 9,
    title: 'Login Request',
    shouldSkip: data => !isConfiguringRecipe(data),
    validate: recipeValidate(2),
  },
  {
    id: STEP_IDS.RECIPE_SESSION,
    order: 10,
    title: 'Capture Session',
    shouldSkip: data => !isConfiguringRecipe(data),
    validate: recipeValidate(3),
  },
  {
    id: STEP_IDS.RECIPE_REVIEW,
    order: 11,
    title: 'Review & Save',
    isFinal: true,
    shouldSkip: data => !isConfiguringRecipe(data),
    validate: recipeValidate(4),
  },
];

/**
 * Initial form data for wizard.
 * Reset to these values when modal opens or wizard resets.
 *
 * `credential` defaults to `{ mode: 'skip' }` — the safe default that never
 * blocks seed creation. `useWizard.reset()` restores this on open/close,
 * clearing any secrets the user typed (security P2-2).
 */
export const DEFAULT_SEED_FORM_DATA: SeedWizardFormData = {
  seedType: null,
  textInput: '',
  credential: { mode: 'skip' },
};
